import React from 'react';

const sections = [
  {
    title: 'Infrastructure & Hosting',
    body: [
      'Complin.AI is hosted on Google Cloud / Firebase, leveraging Google’s secure infrastructure.',
      'Data is stored in Firebase Cloud Firestore and Firebase Storage, and authentication is handled via Firebase Authentication.',
      'Google Cloud infrastructure is independently audited and maintains certifications such as ISO 27001, SOC 1/2/3, and PCI-DSS for underlying services.',
      'Complin.AI builds on these controls and follows industry best practices for application security.'
    ]
  },
  {
    title: 'Data Encryption',
    body: [
      'We encrypt data in transit and at rest.',
      'All communication between app, APIs, and database uses HTTPS/TLS.',
      'Data stored in Firebase is encrypted at rest using Google-managed encryption keys.',
      'Passwords (for email/password sign-in) are hashed using secure, industry-standard algorithms—we do not store plaintext passwords.'
    ]
  },
  {
    title: 'Authentication & Access Control',
    body: [
      'We provide secure authentication and restrict access to only what is needed.',
      'Supported login methods include email & password and Google Single Sign-On (SSO).',
      'Role- and organization-based access control ensures only authorized users within an organization can view their own locations, evidence, and records.',
      'Sessions are managed securely; tokens are revocable.',
      'We recommend customers use strong, unique passwords, limit access based on staff roles, and remove users promptly when roles change.'
    ]
  },
  {
    title: 'Data Handling & AI Processing',
    body: [
      'Complin.AI processes certain content using AI to make compliance easier and more reliable.',
      'Users may upload photos, reports, compliance evidence, contracts, and permits.',
      'We use Google Vision API and internal logic to extract text (such as dates, certificate info, vendor details) and suggest compliance obligations and due dates.',
      'Data is transmitted securely to Google’s APIs. We do not sell or share documents or images for advertising, and we do not use customer content to train public models.'
    ]
  },
  {
    title: 'Data Segregation & Tenant Isolation',
    body: [
      'Each organization’s data is logically isolated.',
      'Access is scoped by organization/account, and evidence, contracts, and compliance records are not cross-visible between customers.'
    ]
  },
  {
    title: 'Backups & Reliability',
    body: [
      'We rely on Firebase’s managed infrastructure for durability and availability.',
      'Data is replicated within Google Cloud for resilience, and we maintain backup strategies to support disaster recovery scenarios.',
      'In the event of a critical incident, our team can restore service and assist with recovery as needed.'
    ]
  },
  {
    title: 'Logging & Monitoring',
    body: [
      'Key actions—such as logins, uploads, and changes to records—may be logged to help detect issues and protect against abuse.',
      'Logs are used for security monitoring, debugging and reliability improvements, and incident investigation when required.'
    ]
  },
  {
    title: 'Vulnerability Management',
    body: [
      'We continuously improve our security posture through regular dependency and library updates, enforcing the principle of least privilege for internal tools and admin access, and maintaining separation between production and development environments.'
    ]
  },
  {
    title: 'Report a Vulnerability',
    body: [
      'If you believe you’ve found a security issue affecting Complin.AI, please email security@complin.ai and include details so we can reproduce and assess impact.',
      'We ask that you do not publicly disclose issues before we’ve had a chance to investigate and resolve them. We appreciate responsible disclosure and take all reports seriously.'
    ]
  },
  {
    title: 'Customer Responsibilities',
    body: [
      'Security is a shared responsibility. We ask customers to control who has access to their Complin.AI organization, configure appropriate roles internally, educate staff not to upload unnecessary sensitive personal data, and notify us immediately if unauthorized access is suspected.'
    ]
  },
  {
    title: 'Relationship to Privacy & Terms',
    body: [
      'This Security page explains how we protect data.',
      'For details on what data we collect, why we collect it, how long we keep it, and your rights and choices, please review our Privacy Policy and Terms & Conditions.'
    ]
  }
];

const SecurityPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-wider text-gray-500">Security</p>
          <h1 className="text-3xl font-semibold mt-2 text-gray-900">Security at Complin.AI</h1>
          <p className="mt-3 text-sm text-gray-600">Last updated: November 6, 2025</p>
          <p className="mt-1 text-sm text-gray-600">Maintained by: SNN Management Inc, Salt Lake City, Utah, USA</p>
          <p className="mt-4 text-sm text-gray-700">
            At Complin.AI, security is not a feature—it’s the foundation. Our platform manages compliance records, contracts, inspection evidence, and other sensitive operational data. This page explains how we protect that data across infrastructure, access, storage, and AI processing.
          </p>
          <p className="mt-3 text-sm text-gray-700">
            If you have any questions or need a detailed security brief for your organization, contact us at <a href="mailto:security@complin.ai" className="text-primary-600 hover:text-primary-700">security@complin.ai</a>.
          </p>
        </header>

        <section className="space-y-8 text-sm leading-6 text-gray-700">
          {sections.map((section) => (
            <article key={section.title}>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
              {section.body.map((paragraph, idx) => (
                <p key={idx} className="mb-2 whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </article>
          ))}
        </section>
      </div>
    </div>
  );
};

export default SecurityPage;

